import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from "@microsoft/signalr";
import { format } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { handleGetOrdersRequest } from "store/modules/Orders/thunk.store";
import type { OrderProps } from "store/modules/Orders/types.store";
import { parsePersistedDateFromObject } from "utils/parsePersistedDateFromObject";
import useAlert from "hooks/useAlert";

const RECONNECT_DELAYS_MS = [0, 3000, 5000, 10000, 15000, 30000, 60000, 120000];

const hubLogLevel =
  process.env.NODE_ENV === "development"
    ? LogLevel.Information
    : LogLevel.Error;

export const useOrders = (selectedDealer: string | null) => {
  const auth = useAuth();
  const accessTokenRef = useRef(auth.user?.access_token);

  const alertPlayer = useAlert();
  const dispatch = useAppDispatch();
  const ordersHubRef = useRef<HubConnection | null>(null);
  const isInitializingRef = useRef(false);
  const isMountedRef = useRef(true);
  const selectedDealerRef = useRef<string | null>(null);
  const shouldForceCloseConnectionRef = useRef(false);
  const initializeConnectionRef = useRef<() => Promise<void>>(async () => {});

  const [ordersSignal, setOrdersSignal] = useState<any[]>();
  const [lastOrderSignal, setLastOrderSignal] = useState<any>();
  const [openModalFinallyOrder, setOpenModalFinallyOrder] = useState(true);
  const [
    openModalFinallyOrderDescription,
    setOpenModalFinallyOrderDescription,
  ] = useState(false);
  const [openModalCode, setOpenModalCode] = useState(
    false || !localStorage.getItem("@ctech:firstAccessiWthoutRequest"),
  );
  const { allOrders } = useAppSelector((state) => state.orders);

  useEffect(() => {
    accessTokenRef.current = auth.user?.access_token;
  }, [auth.user?.access_token]);

  useEffect(() => {
    isMountedRef.current = true;
    selectedDealerRef.current = selectedDealer;

    return () => {
      isMountedRef.current = false;
    };
  }, [selectedDealer]);

  const closeConnection = useCallback(async () => {
    if (
      ordersHubRef.current &&
      ordersHubRef.current.state === HubConnectionState.Connected
    ) {
      try {
        // eslint-disable-next-line no-console
        console.log("fechando conexão dos pedidos");

        ordersHubRef.current.off("OrderChanged");

        await ordersHubRef.current.stop();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("error ao fechar a conexão", error);
      } finally {
        ordersHubRef.current = null;
      }
    }
  }, []);

  const typeOrders = (type: string) => {
    const ordersSignalFilter =
      ordersSignal
        ?.filter((x: any) => x.dealerState === type)
        ?.filter(
          (order: any) =>
            order.dealer.id === (selectedDealer || order.dealer.id),
        ) || [];

    if (type === "Finalized") {
      const ordersSignalFinalized = ordersSignalFilter.filter(
        (order) =>
          Number(format(new Date(order.createdAt), "dd")) ===
          Number(format(new Date(), "dd")),
      );

      return ordersSignalFinalized;
    }

    return ordersSignalFilter;
  };

  useEffect(() => {
    if (allOrders) {
      const orders = [...allOrders].reverse();

      setOrdersSignal(orders);
    }
  }, [allOrders]);

  const initializeConnection = useCallback(async () => {
    if (
      isInitializingRef.current ||
      !isMountedRef.current ||
      !selectedDealer ||
      !auth.isAuthenticated ||
      !accessTokenRef.current
    ) {
      return;
    }

    isInitializingRef.current = true;

    try {
      await closeConnection();

      // eslint-disable-next-line no-console
      console.log("inicializando conexão dos pedidos");

      if (!isMountedRef.current || !selectedDealer) {
        return;
      }

      const hub = new HubConnectionBuilder()
        .withUrl(`${process.env.REACT_APP_ORDERS_API_URL}order-hubs/orders`, {
          withCredentials: false,
          accessTokenFactory: () => accessTokenRef.current ?? "",
        })
        .configureLogging(hubLogLevel)
        .withAutomaticReconnect(RECONNECT_DELAYS_MS)
        .build();

      ordersHubRef.current = hub;

      hub.onreconnected(async () => {
        // eslint-disable-next-line no-console
        console.log("conexão restabelecida");

        const dealerId = selectedDealerRef.current;
        if (
          isMountedRef.current &&
          ordersHubRef.current &&
          ordersHubRef.current.state === HubConnectionState.Connected &&
          dealerId
        ) {
          await ordersHubRef.current.invoke(
            "SubscribeForNotifications",
            dealerId,
          );
          await dispatch(handleGetOrdersRequest(dealerId));
        }
      });

      hub.onclose(async () => {
        // eslint-disable-next-line no-console
        console.log("conexão fechada de pedidos");

        if (isMountedRef.current && !shouldForceCloseConnectionRef.current) {
          await initializeConnectionRef.current();
        }
      });

      hub.on("OrderChanged", (data: OrderProps) => {
        parsePersistedDateFromObject(data, "createdAt");
        setLastOrderSignal(data);
      });

      await hub.start();

      // eslint-disable-next-line no-console
      console.log("conexão dos pedidos iniciada");

      if (
        isMountedRef.current &&
        !shouldForceCloseConnectionRef.current &&
        ordersHubRef.current &&
        ordersHubRef.current.state === HubConnectionState.Connected &&
        selectedDealer
      ) {
        await ordersHubRef.current.invoke(
          "SubscribeForNotifications",
          selectedDealer,
        );
        await dispatch(handleGetOrdersRequest(selectedDealer));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error ao iniciar o SignalR:", error);

      const hubToStop = ordersHubRef.current;
      if (hubToStop) {
        try {
          hubToStop.off("OrderChanged");
          await hubToStop.stop();
        } catch {
          //
        }
        ordersHubRef.current = null;
      }

      const retryDelay = Math.min(5000 * 2 ** Math.random(), 30000);

      setTimeout(() => {
        if (isMountedRef.current && !shouldForceCloseConnectionRef.current) {
          initializeConnectionRef.current().catch(() => {});
        }
      }, retryDelay);
    } finally {
      isInitializingRef.current = false;
    }
  }, [selectedDealer, auth.isAuthenticated, closeConnection, dispatch]);

  useEffect(() => {
    initializeConnectionRef.current = initializeConnection;
  }, [initializeConnection]);

  useEffect(() => {
    if (!selectedDealer) {
      closeConnection().catch(() => {});
      dispatch(handleGetOrdersRequest(null));
      return undefined;
    }

    if (!auth.isAuthenticated || !auth.user?.access_token) {
      return undefined;
    }

    shouldForceCloseConnectionRef.current = false;
    initializeConnection().catch(() => {});

    return () => {
      shouldForceCloseConnectionRef.current = true;
      closeConnection().catch(() => {});
    };
  }, [
    selectedDealer,
    auth.isAuthenticated,
    auth.user?.access_token,
    closeConnection,
    dispatch,
    initializeConnection,
  ]);

  useEffect(() => {
    if (lastOrderSignal) {
      const index = ordersSignal?.findIndex(
        (value: any) => value.id === lastOrderSignal.id,
      );

      if (ordersSignal) {
        const newArray = [...ordersSignal];
        if ((index ?? -1) >= 0) {
          newArray[Number(index)] = lastOrderSignal;
        } else {
          if (lastOrderSignal.dealerState === "New") {
            alertPlayer.alert();
          }
          newArray.push(lastOrderSignal);
        }
        setOrdersSignal(newArray);
      }

      setLastOrderSignal(undefined);
    }
  }, [lastOrderSignal, alertPlayer, ordersSignal]);

  return {
    allOrders,
    setOrdersSignal,
    ordersSignal,
    typeOrders,
    openModalCode,
    setOpenModalCode,
    openModalFinallyOrder,
    setOpenModalFinallyOrder,
    openModalFinallyOrderDescription,
    setOpenModalFinallyOrderDescription,
  };
};
